import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Plus, Edit2, Trash2, DollarSign, Upload, ExternalLink,
  Calendar, TrendingUp, Eye, MousePointerClick, PlayCircle,
  PauseCircle, Image as ImageIcon, Link as LinkIcon, X,
  CheckCircle, AlertCircle, Package
} from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../../../../utils/supabase/info';
import { getSharedSupabaseClient } from '../../services/api';

interface Advert {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  linkUrl?: string;
  duration: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'semi-annual' | 'yearly';
  startDate: string;
  endDate: string;
  isActive: boolean;
  cost: number;
  advertiser: string;
  views: number;
  clicks: number;
  createdAt: string;
}

interface AdvertPricing {
  daily: number;
  weekly: number;
  monthly: number;
  quarterly: number;
  semiAnnual: number;
  yearly: number;
}

interface AdvertManagementProps {
  canManage: boolean;
}

export function AdvertManagement({ canManage }: AdvertManagementProps) {
  const [adverts, setAdverts] = React.useState<Advert[]>([]);
  const [pricing, setPricing] = React.useState<AdvertPricing>({
    daily: 1000,
    weekly: 5000,
    monthly: 15000,
    quarterly: 40000,
    semiAnnual: 70000,
    yearly: 120000
  });
  const [isLoading, setIsLoading] = React.useState(true);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [showPricingModal, setShowPricingModal] = React.useState(false);
  const [editingAdvert, setEditingAdvert] = React.useState<Advert | null>(null);
  const [uploadingImage, setUploadingImage] = React.useState(false);

  // Form state
  const [formData, setFormData] = React.useState({
    title: '',
    description: '',
    imageUrl: '',
    linkUrl: '',
    duration: 'monthly' as const,
    advertiser: '',
    startDate: new Date().toISOString().split('T')[0]
  });

  React.useEffect(() => {
    if (canManage) {
      loadAdverts();
      loadPricing();
    }
  }, [canManage]);

  const loadAdverts = async () => {
    try {
      setIsLoading(true);
      const supabase = await getSharedSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/adverts`,
        {
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-Supabase-Token': session?.access_token || ''
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAdverts(data.adverts || []);
      }
    } catch (error) {
      console.error('Failed to load adverts:', error);
      toast.error('Failed to load adverts');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPricing = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/adverts/pricing`,
        {
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.pricing) {
          setPricing(data.pricing);
        }
      }
    } catch (error) {
      console.error('Failed to load pricing:', error);
    }
  };

  const calculateEndDate = (startDate: string, duration: string): string => {
    const start = new Date(startDate);
    const end = new Date(start);

    switch (duration) {
      case 'daily':
        end.setDate(end.getDate() + 1);
        break;
      case 'weekly':
        end.setDate(end.getDate() + 7);
        break;
      case 'monthly':
        end.setMonth(end.getMonth() + 1);
        break;
      case 'quarterly':
        end.setMonth(end.getMonth() + 3);
        break;
      case 'semi-annual':
        end.setMonth(end.getMonth() + 6);
        break;
      case 'yearly':
        end.setFullYear(end.getFullYear() + 1);
        break;
    }

    return end.toISOString().split('T')[0];
  };

  const handleImageUpload = async (file: File) => {
    if (!file) return;

    try {
      setUploadingImage(true);
      
      // Create FormData for image upload
      const formData = new FormData();
      formData.append('file', file);

      const supabase = await getSharedSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/adverts/upload-image`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-Supabase-Token': session?.access_token || ''
          },
          body: formData
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, imageUrl: data.imageUrl }));
        toast.success('Image uploaded successfully!');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreateAdvert = async () => {
    if (!canManage) {
      toast.error('Permission denied');
      return;
    }

    if (!formData.title || !formData.advertiser) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const endDate = calculateEndDate(formData.startDate, formData.duration);
      const cost = pricing[formData.duration === 'semi-annual' ? 'semiAnnual' : formData.duration as keyof AdvertPricing];

      const supabase = await getSharedSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/adverts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-Supabase-Token': session?.access_token || ''
          },
          body: JSON.stringify({
            ...formData,
            endDate,
            cost
          })
        }
      );

      if (response.ok) {
        toast.success('Advert created successfully!');
        setShowCreateModal(false);
        setFormData({
          title: '',
          description: '',
          imageUrl: '',
          linkUrl: '',
          duration: 'monthly',
          advertiser: '',
          startDate: new Date().toISOString().split('T')[0]
        });
        loadAdverts();
      } else {
        throw new Error('Failed to create advert');
      }
    } catch (error) {
      console.error('Create advert failed:', error);
      toast.error('Failed to create advert');
    }
  };

  const handleToggleStatus = async (advertId: string, currentStatus: boolean) => {
    if (!canManage) return;

    try {
      const supabase = await getSharedSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/adverts/${advertId}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-Supabase-Token': session?.access_token || ''
          },
          body: JSON.stringify({ isActive: !currentStatus })
        }
      );

      if (response.ok) {
        toast.success(`Advert ${!currentStatus ? 'activated' : 'deactivated'}`);
        loadAdverts();
      }
    } catch (error) {
      console.error('Toggle status failed:', error);
      toast.error('Failed to update advert status');
    }
  };

  const handleDeleteAdvert = async (advertId: string) => {
    if (!canManage) return;
    
    if (!confirm('Are you sure you want to delete this advert?')) return;

    try {
      const supabase = await getSharedSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/adverts/${advertId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-Supabase-Token': session?.access_token || ''
          }
        }
      );

      if (response.ok) {
        toast.success('Advert deleted');
        loadAdverts();
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete advert');
    }
  };

  const handleUpdatePricing = async () => {
    if (!canManage) return;

    try {
      const supabase = await getSharedSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/adverts/pricing`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-Supabase-Token': session?.access_token || ''
          },
          body: JSON.stringify({ pricing })
        }
      );

      if (response.ok) {
        toast.success('Pricing updated successfully!');
        setShowPricingModal(false);
      }
    } catch (error) {
      console.error('Update pricing failed:', error);
      toast.error('Failed to update pricing');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getDurationLabel = (duration: string) => {
    const labels: Record<string, string> = {
      daily: 'Daily (1 day)',
      weekly: 'Weekly (7 days)',
      monthly: 'Monthly (30 days)',
      quarterly: 'Quarterly (3 months)',
      'semi-annual': 'Semi-Annual (6 months)',
      yearly: 'Yearly (12 months)'
    };
    return labels[duration] || duration;
  };

  if (!canManage) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-600 font-semibold">
          You don't have permission to manage advertisements
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-gray-900">Advertisement Management</h2>
          <p className="text-sm text-gray-600 font-semibold mt-1">
            Manage app advertisements and pricing
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowPricingModal(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-blue-600 transition-colors"
          >
            <DollarSign className="w-4 h-4" />
            Pricing
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-green-500 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-green-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Advert
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
          <Package className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-black">{adverts.length}</p>
          <p className="text-sm font-semibold opacity-90">Total Adverts</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white">
          <PlayCircle className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-black">
            {adverts.filter(a => a.isActive).length}
          </p>
          <p className="text-sm font-semibold opacity-90">Active</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
          <Eye className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-black">
            {adverts.reduce((sum, a) => sum + (a.views || 0), 0).toLocaleString()}
          </p>
          <p className="text-sm font-semibold opacity-90">Total Views</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
          <MousePointerClick className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-black">
            {adverts.reduce((sum, a) => sum + (a.clicks || 0), 0).toLocaleString()}
          </p>
          <p className="text-sm font-semibold opacity-90">Total Clicks</p>
        </div>
      </div>

      {/* Adverts List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto" />
          <p className="text-gray-600 font-semibold mt-4">Loading adverts...</p>
        </div>
      ) : adverts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-2xl">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 font-semibold">No adverts yet</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 px-6 py-2 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 transition-colors"
          >
            Create First Advert
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {adverts.map(advert => (
            <div
              key={advert.id}
              className="bg-white border-2 border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all"
            >
              <div className="flex gap-6">
                {/* Thumbnail */}
                <div className="flex-shrink-0">
                  {advert.imageUrl ? (
                    <img
                      src={advert.imageUrl}
                      alt={advert.title}
                      className="w-32 h-32 object-cover rounded-xl border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl flex items-center justify-center border-2 border-gray-200">
                      <ImageIcon className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-black text-gray-900 mb-1">
                        {advert.title}
                      </h3>
                      <p className="text-sm text-gray-600 font-semibold">
                        By {advert.advertiser}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {advert.isActive ? (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-black flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          ACTIVE
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-black">
                          INACTIVE
                        </span>
                      )}
                    </div>
                  </div>

                  {advert.description && (
                    <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                      {advert.description}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm mb-4">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="font-semibold text-gray-600">
                        {getDurationLabel(advert.duration)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span className="font-semibold text-gray-600">
                        {formatCurrency(advert.cost)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Eye className="w-4 h-4 text-gray-400" />
                      <span className="font-semibold text-gray-600">
                        {advert.views?.toLocaleString() || 0} views
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MousePointerClick className="w-4 h-4 text-gray-400" />
                      <span className="font-semibold text-gray-600">
                        {advert.clicks?.toLocaleString() || 0} clicks
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="font-semibold">
                      {new Date(advert.startDate).toLocaleDateString()} - {new Date(advert.endDate).toLocaleDateString()}
                    </span>
                    {new Date(advert.endDate) < new Date() && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full font-bold">
                        EXPIRED
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleToggleStatus(advert.id, advert.isActive)}
                    className={`p-2 rounded-xl font-bold transition-colors ${
                      advert.isActive
                        ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                    title={advert.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {advert.isActive ? (
                      <PauseCircle className="w-5 h-5" />
                    ) : (
                      <PlayCircle className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDeleteAdvert(advert.id)}
                    className="p-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-xl font-bold transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Advert Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4 flex items-center justify-between rounded-t-3xl">
                <h3 className="text-xl font-black">Create New Advert</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">
                    Ad Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-green-500 focus:outline-none"
                    placeholder="Enter ad title"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-green-500 focus:outline-none resize-none"
                    rows={3}
                    placeholder="Describe your ad"
                  />
                </div>

                {/* Advertiser */}
                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">
                    Advertiser Name *
                  </label>
                  <input
                    type="text"
                    value={formData.advertiser}
                    onChange={(e) => setFormData({ ...formData, advertiser: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-green-500 focus:outline-none"
                    placeholder="Company or person name"
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">
                    Ad Image
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(file);
                      }}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="flex-1 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl font-semibold text-gray-600 hover:border-green-500 hover:text-green-600 cursor-pointer transition-colors flex items-center justify-center gap-2"
                    >
                      <Upload className="w-5 h-5" />
                      {uploadingImage ? 'Uploading...' : 'Upload Image'}
                    </label>
                  </div>
                  {formData.imageUrl && (
                    <div className="mt-2 relative">
                      <img
                        src={formData.imageUrl}
                        alt="Preview"
                        className="w-full h-32 object-cover rounded-xl"
                      />
                      <button
                        onClick={() => setFormData({ ...formData, imageUrl: '' })}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Link URL */}
                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">
                    Link URL (Optional)
                  </label>
                  <div className="flex gap-2">
                    <LinkIcon className="w-5 h-5 text-gray-400 mt-3" />
                    <input
                      type="url"
                      value={formData.linkUrl}
                      onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                      className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-green-500 focus:outline-none"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">
                    Campaign Duration *
                  </label>
                  <select
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value as any })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-green-500 focus:outline-none"
                  >
                    <option value="daily">Daily - {formatCurrency(pricing.daily)}</option>
                    <option value="weekly">Weekly - {formatCurrency(pricing.weekly)}</option>
                    <option value="monthly">Monthly - {formatCurrency(pricing.monthly)}</option>
                    <option value="quarterly">Quarterly - {formatCurrency(pricing.quarterly)}</option>
                    <option value="semi-annual">Semi-Annual - {formatCurrency(pricing.semiAnnual)}</option>
                    <option value="yearly">Yearly - {formatCurrency(pricing.yearly)}</option>
                  </select>
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-green-500 focus:outline-none"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                {/* Cost Preview */}
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-black text-gray-700">Campaign Cost:</span>
                    <span className="text-2xl font-black text-green-600">
                      {formatCurrency(pricing[formData.duration === 'semi-annual' ? 'semiAnnual' : formData.duration as keyof AdvertPricing])}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 font-semibold mt-2">
                    End Date: {calculateEndDate(formData.startDate, formData.duration)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateAdvert}
                    disabled={!formData.title || !formData.advertiser || uploadingImage}
                    className="flex-1 px-6 py-3 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create Advert
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pricing Modal */}
      <AnimatePresence>
        {showPricingModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPricingModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-lg"
            >
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 flex items-center justify-between rounded-t-3xl">
                <h3 className="text-xl font-black">Manage Pricing</h3>
                <button
                  onClick={() => setShowPricingModal(false)}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">Daily Rate (₦)</label>
                  <input
                    type="number"
                    value={pricing.daily}
                    onChange={(e) => setPricing({ ...pricing, daily: Number(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">Weekly Rate (₦)</label>
                  <input
                    type="number"
                    value={pricing.weekly}
                    onChange={(e) => setPricing({ ...pricing, weekly: Number(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">Monthly Rate (₦)</label>
                  <input
                    type="number"
                    value={pricing.monthly}
                    onChange={(e) => setPricing({ ...pricing, monthly: Number(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">Quarterly Rate (₦)</label>
                  <input
                    type="number"
                    value={pricing.quarterly}
                    onChange={(e) => setPricing({ ...pricing, quarterly: Number(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">Semi-Annual Rate (₦)</label>
                  <input
                    type="number"
                    value={pricing.semiAnnual}
                    onChange={(e) => setPricing({ ...pricing, semiAnnual: Number(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-black text-gray-700 mb-2">Yearly Rate (₦)</label>
                  <input
                    type="number"
                    value={pricing.yearly}
                    onChange={(e) => setPricing({ ...pricing, yearly: Number(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowPricingModal(false)}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdatePricing}
                    className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 transition-colors"
                  >
                    Save Pricing
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}